import {useState} from 'react';

import {Post} from '../lib/api/POST/Post';
import {PostRoutes} from '../lib/api/POST/PostRoutes';
import {SecureObjectUpload} from '../lib/api/RequestBodies/secure-s3-file-manipulation/SharedSecureS3FileManipulations';
import {SecureObjectUploadResponse} from '../types/shared-S3-file-manipulation';

import {useApiWrite} from './useApiWrite';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const uuidv4 = require('uuid').v4;

enum ObjectTypes {
  profile_picture = 'profile_picture',
  resume = 'resume',
  media = 'company_media',
  logo = 'company_logo',
}

interface Props {
  scope: 'companies' | 'applicants';
  ownerId: string;
  objectType: 'profile_picture' | 'resume' | 'media' | 'logo';
  file: File | null;
  checksum: string | null;
  onSuccess: (response: Response, logoId: string | null) => void;
}

export const useSecureS3Upload = ({scope, ownerId, objectType, file, checksum, onSuccess}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const uuId = uuidv4();
  const filename = `${uuId}.${file?.type.split('/').pop()}`;
  const filesize = file?.size || 0;

  const {
    makeRequest: getPresignedUrl,
    error: getPresignedUrlError,
    isLoading: isGetPresignedUrlLoading,
  } = useApiWrite<Post.SecureObjectUpload, SecureObjectUploadResponse>({
    method: 'post',
    path: PostRoutes[Post.SecureObjectUpload],
    onSuccess: (data: SecureObjectUploadResponse) => {
      uploadUsingPresignedUrl(data);
    },
    disableLoadingOverlay: true,
  });

  const makeRequest = () => {
    setError(null);
    if (!file) {
      setError(new Error('No file to upload'));
      return;
    }

    const body: SecureObjectUpload = {
      secureObjectUpload: {
        scope: scope,
        ownerId: ownerId,
        objectType: objectType,
        filename: filename,
        contentType: file.type,
        byteSize: file.size,
        checksum: checksum,
      },
    };

    getPresignedUrl(body);
  };

  const uploadUsingPresignedUrl = async (data: SecureObjectUploadResponse) => {
    try {
      const baseHeaders = {
        'Content-Type': file?.type as string,
      };

      const contentHeaders = {
        ...baseHeaders,
        'Content-MD5': checksum,
        'Content-Length': filesize,
      };

      const response = await fetch(data.secureObjectUpload.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: checksum ? contentHeaders : baseHeaders,
      });

      if (!response.ok) {
        setIsLoading(false);
        const responseText = await response.text();
        setError(new Error(`Failed to upload file: ${responseText}`));
      }

      setIsLoading(false);

      const s3BaseUrl = process.env.REACT_APP_OBJECT_STORAGE_BASE_URL;
      const urlToSave = `${s3BaseUrl}/${scope}/${ownerId}/${ObjectTypes[objectType]}/${filename}`;

      onSuccess({...response, url: urlToSave}, data.secureObjectUpload.signedId);

      return response;
    } catch (error) {
      setIsLoading(false);
      setError(error as Error);
    }
  };

  return {
    makeRequest,
    isLoading: isLoading || isGetPresignedUrlLoading,
    error: error || getPresignedUrlError,
  };
};
